import { TitleCasePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { AppDataService } from '../../core/services/app-data.service';
import { ReportService } from '../../core/services/report.service';
import { ExportService } from '../../core/services/export.service';
import { formatDate } from '../../core/utils/format.util';
import { matchesItemQuery } from '../../core/utils/item-filter.util';
import { environment } from '../../environments/environment';
import { ReportDateRangeComponent } from '../report-date-range/report-date-range.component';
import { ReportDateRangeService } from '../../report-date-range.service';

@Component({
  selector: 'app-stock-movement-report',
  standalone: true,
  imports: [TitleCasePipe, FormsModule, RouterLink, ReportDateRangeComponent],
  templateUrl: './stock-movement.component.html',
  styleUrl: '../../report-shared.css',
})
export class StockMovementReportComponent implements OnInit {
  private readonly appData = inject(AppDataService);
  private readonly reports = inject(ReportService);
  private readonly exporter = inject(ExportService);
  protected readonly dateRange = inject(ReportDateRangeService);
  protected readonly formatDate = formatDate;

  private readonly range = computed(() => ({
    startDate: this.dateRange.appliedStartDate(),
    endDate: this.dateRange.appliedEndDate(),
  }));

  protected readonly itemNameFilter = signal('');

  private readonly allMovements = computed(() =>
    this.reports.getStockMovement(this.range()),
  );

  protected readonly movements = computed(() =>
    this.allMovements().filter((row) =>
      matchesItemQuery(this.itemNameFilter(), row.itemName, row.itemCode),
    ),
  );

  protected readonly movementStats = computed(() => {
    const movements = this.movements();
    const incoming = movements
      .filter((row) => row.movementType === 'received')
      .reduce((sum, row) => sum + row.quantity, 0);
    const outgoing = movements
      .filter((row) => row.movementType === 'issued')
      .reduce((sum, row) => sum + row.quantity, 0);

    return { incoming, outgoing, net: incoming - outgoing };
  });

  protected readonly maxQuantity = computed(() =>
    Math.max(...this.movements().map((row) => row.quantity), 1),
  );

  protected sparkHeight(quantity: number): number {
    return Math.round((quantity / this.maxQuantity()) * 60);
  }

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.appData.refreshAll();
    }
  }

  exportData(): void {
    this.exporter.downloadCsv(
      `stock-movement-${new Date().toISOString().slice(0, 10)}`,
      this.movements().map((row) => ({ ...row })),
    );
  }
}
